<template>
  <div>
    <PageHeader title="Traspaso" :breadcrum-routes="pageHeaderRoutes">
      <template #tags>
        <a-tag
          :color="
            stateTag === 'CANCELADO'
              ? 'error'
              : stateTag === 'EJECUTADO'
              ? 'success'
              : 'warning'
          "
        >
          {{ stateTag }}
        </a-tag>
        <a-tag color="processing">ID: {{ ticketId }}</a-tag>
      </template>
      <template v-if="userRole != 'rrhh'" #buttons>
        <a-button
          v-if="stateTag != 'CANCELADO' && stateTag != 'EJECUTADO'"
          @click="openCancelModal"
          :loading="loading"
        >
          Cancelar
        </a-button>
        <a-button
          class="ml-1"
          type="primary"
          @click="openExecuteModal"
          :loading="loading"
          v-if="stateTag === 'EN ASEGURADORA'"
        >
          Ejecutar
        </a-button>
      </template>
    </PageHeader>

    <StepperState v-bind="stepperData" />

    <a-card class="mt-3">
      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :md="12">
          <TicketData v-bind="ticketDetails" />

          <MessageToInsurance
            v-if="insuranceMessage != null && userRole !== 'rrhh'"
            class="mt-3"
            :message="insuranceMessage"
          />

          <a-card
            class="mt-3"
            v-if="userRole != 'rrhh'"
            title="Casos asociados"
            bodyStyle="padding: 24px 24px 24px 24px; border: 1px solid #f0f0f0; border-top-width: 0px;"
            :bordered="false"
          >
            <div v-if="cases.length == 0">
              <a-typography-text>No existen casos</a-typography-text>
            </div>

            <a-row
              type="flex"
              justify="space-between"
              v-for="c in cases"
              :key="c.id"
            >
              <a-col :span="6">
                <a-typography-text>{{ c.state }}</a-typography-text>
              </a-col>
              <a-col>
                <tags :stateId="c.tag" />
              </a-col>
              <a-col>
                <a-button type="link" @click="redirectCase(c)">
                  Ir a caso
                </a-button>
              </a-col>
              <a-divider class="my-2" v-if="cases.length > 1" />
            </a-row>
          </a-card>
        </a-col>
        <a-col :xs="24" :md="12">
          <Records v-bind="recordsData" />

          <TicketData
            class="mt-3"
            title="Pólizas de origen"
            :data="originPolicies"
          />

          <TicketData
            class="mt-3"
            title="Pólizas de destino"
            :data="destinyPolicies"
          />

          <AttachedDocuments
            v-if="userRole != 'rrhh' && ticketOptionalFiles.length > 0"
            class="mt-3"
            title="Archivos opcionales"
            :data="ticketOptionalFiles"
            :loading="loadingOptionalFiles"
          />

          <Comments
            v-if="userRole != 'rrhh'"
            class="mt-3"
            :idToTicket="ticketId"
            :comments="comments"
            @new_comment="getNewCommment"
          />
        </a-col>
      </a-row>
    </a-card>

    <CancelModal
      ref="transferModalRef"
      @onCancelTransfer="cancelTransferTicker"
    />

    <ExecuteModal
      ref="executeModalRef"
      @onExecuteTransfer="executeTransferTicker"
    />
  </div>
</template>

<script setup>
import moment from 'moment';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { computed, onMounted, ref } from 'vue';
import notification from '@/utils/notifications';
import rutFormatter from '@/utils/Ruts';
import PageHeader from '@/components/page-header.vue';
import StepperState from '@/components/stepper-state.vue';
import TicketData from '@/components/tickets/ticket-data.vue';
import MessageToInsurance from '@/components/tickets/insurer-message.vue';
import AttachedDocuments from '@/components/tickets/attached-documents.vue';
import Comments from '@/components/tickets/comments.vue';
import Records from '@/components/tickets/records.vue';
import CancelModal from '@/components/transfer/transfer-cancel-modal.vue';
import ExecuteModal from '@/components/transfer/transfer-execute-modal.vue';
import Tags from '@/components/tags.vue';
import DateFormmater from '@/utils/dateFormatter';
import parseDate from '@/utils/dateFormatter';
import openNotificationWithIcon from '@/utils/notifications';
import {
  FETCH_TICKET_ID,
  FETCH_EMPLOYEE_TICKET,
  CANCEL_TICKET_TRANSFER,
  EXECUTE_TICKET_TRANSFER,
  FETCH_CASES,
  FETCH_TICKET_COMMENTS_ID,
  FETCH_USER_BY_ID,
  FETCH_TRANSFER_INFORMATION,
  FETCH_TRANSFER_GROUPS,
  GET_LOGGED_USER,
  FETCH_TICKET_OPTIONAL_FILES,
  GET_TICKET_OPTIONAL_FILES
} from '@/store/types';

const store = useStore();
const route = useRoute();
const ticketId = route.params.id;
const loading = ref(false);
const employeeData = ref({});
const brokerName = ref('');
const transferInfo = ref({});
const groupData = ref([]);

const userRole = computed(
  () => store.getters[GET_LOGGED_USER]?.roleName || 'rrhh'
);

// * Page Header
const pageHeaderRoutes = [
  {
    path: '#',
    breadcrumbName: 'Traspasos'
  }
];

const stateTag = computed(() => {
  const { states } = ticketData.value;

  const map = {
    1: 'CREADO',
    7: 'EN CORREDORA',
    8: 'EN ASEGURADORA',
    3: 'EJECUTADO',
    4: 'CANCELADO',
    5: 'EJECUTADO' // Ver si se puede eliminar este
  };

  if (states) {
    return map[states[states.length - 1].state.id];
  }
  return 'vacio';
});

// * Stepper State
const stepperData = computed(() => {
  const data = [
    {
      index: 1,
      title: 'Creado',
      description: ''
    },
    {
      index: 8,
      title: 'En revisión aseguradora',
      description: ''
    },
    {
      index: 3,
      title: 'Ejecutado',
      description: ''
    }
  ];
  let isCancelled = false;
  let current = 0;

  const { states } = ticketData.value;

  const map = {
    1: 0,
    8: 1,
    3: 2,
    5: 2
  };

  if (states) {
    states.forEach((state) => {
      const { id } = state.state;
      if (id != 4) {
        data[map[id]].description = parseDate(state.createdAt);
      } else {
        isCancelled = true;
        current = states.length - 2;
      }
    });

    if (!isCancelled) {
      current = states.length - 1;
    }
  }

  return {
    data,
    isCancelled,
    current
  };
});

// * Ticket data
const ticketDetails = computed(() => {
  const { firstName, firstSurname, fullname, rut, email } =
    employeeData.value.employee;
  const { startDate, broker, company } = ticketData.value;

  return {
    title: 'Datos ticket',
    data: [
      {
        key: 'Broker',
        value: broker.name
      },
      {
        key: 'Inicio de vigencia',
        value: moment(startDate).format('DD/MM/YYYY')
      },
      {
        key: 'Nombre',
        value:
          fullname ||
          (firstName && firstSurname ? `${firstName} ${firstSurname}` : '')
      },
      {
        key: 'RUT',
        value: rutFormatter.formatRut(rut)
      },
      {
        key: 'Correo',
        value: email
      },
      {
        key: 'Empresa',
        value: company.businessName
      },
      {
        key: 'Grupos origen',
        value: groupData.value.oldGroupCompanies?.map((g) => g.name).join(', ')
      },
      {
        key: 'Grupos destino',
        value: groupData.value.newGroupCompanies?.map((g) => g.name).join(', ')
      }
    ],
    loadTicket: false
  };
});

const insuranceMessage = ref('');

const ticketOptionalFiles = ref([]);
const loadingOptionalFiles = ref(false);

const getTicketOptionalFiles = async () => {
  loadingOptionalFiles.value = true;
  try {
    if (userRole.value != 'rrhh') {
      await store.dispatch(FETCH_TICKET_OPTIONAL_FILES, ticketId);
    }
    ticketOptionalFiles.value = await store.getters[GET_TICKET_OPTIONAL_FILES];
    loadingOptionalFiles.value = false;
  } catch (error) {
    openNotificationWithIcon(
      'error',
      'Error',
      `Error al cargar archivos opcionales del Ticket ${ticketId}. ${error}`
    );
    loadingOptionalFiles.value = false;
  }
};

// * Comentarios
const comments = ref([]);

const getNewCommment = (value) => {
  comments.value.push({
    description: parseDate(value.description),
    title: value.title,
    author: brokerName.value,
    date: value.description
  });
};

const getCommentsByTicketId = async () => {
  const response_comments = await store
    .dispatch(FETCH_TICKET_COMMENTS_ID, ticketId)
    .catch((e) => e.response);

  for (let i = 0; i < response_comments.data.length; i += 1) {
    const element = response_comments.data[i];
    const brokerName = await getBroker(response_comments.data[i].brokerId);
    comments.value.push({
      description: parseDate(element.createdAt),
      title: element.message,
      author: brokerName,
      date: element.createdAt
    });
  }
};

const getBroker = async (id) => {
  const response = await store.dispatch(FETCH_USER_BY_ID, id);
  return response.data.body.name;
};

// * Historial
const recordsData = computed(() => {
  const formattedStates =
    ticketData.value.states?.map((state) => ({
      title: state?.state?.tag,
      description: state?.createdAt ? new Date(state.createdAt) : ''
    })) || [];

  const formattedComments =
    comments.value?.map((comment) => ({
      title: 'Nuevo comentario',
      description: comment?.date ? new Date(comment.date) : ''
    })) || [];

  const records = [...formattedStates, ...formattedComments];

  const orderedRecords = records.sort((a, b) => a?.description - b?.description);

  return {
    title: 'Historial',
    loading: false,
    data: orderedRecords.map((record) => ({
      title: record?.title,
      description: record?.description ? DateFormmater(record.description) : ''
    }))
  };
});

// * Polizas de origen
const originPolicies = computed(() => {
  const { originPolicies } = transferInfo.value;

  const result = originPolicies.flatMap((policy) => {
    return [
      {
        key: 'Póliza',
        value: policy.numberPolicy
      },
      {
        key: 'Coberturas',
        value: policy.coverages
          .map((coverage) => `${coverage.name} (${coverage.groupName})`)
          .join(', ')
      }
    ];
  });

  return result;
});

// * Polizas de destino
const destinyPolicies = computed(() => {
  const { destinyPolicies } = transferInfo.value;

  const result = destinyPolicies.flatMap((policy) => {
    return [
      {
        key: 'Póliza',
        value: policy.numberPolicy
      },
      {
        key: 'Coberturas',
        value: policy.coverages
          .map((coverage) => `${coverage.name} (${coverage.groupName})`)
          .join(', ')
      }
    ];
  });

  return result;
});

// Casos
const router = useRouter();
const cases = ref([]);

const getAllCasesByTicketId = async () => {
  cases.value = [];

  const responseCases = await store
    .dispatch(FETCH_CASES, ticketId)
    .catch((e) => e.response);

  for (let i = 0; i < responseCases.data.length; i += 1) {
    const obj = {
      id: '',
      tag: '',
      state: '',
      typeCaseId: 0
    };

    const caso = responseCases.data[i];

    obj.id = caso.id;
    obj.tag = caso.states
      .sort((a, b) => {
        return b.id - a.id;
      })[0]
      .state.id.toString();
    obj.state = caso.typeCase.tag;
    obj.typeCaseId = caso.typeCaseId;

    cases.value.push(obj);
  }
};

const redirectCase = (value) => {
  const ticketId = value.id;

  const ticketType =
    value.state === 'Out of Time'
      ? 'fuera_de_tiempo'
      : value.state.toLowerCase();

  switch (ticketType) {
    case 'fuera de tiempo: traspaso individual':
      router.push({ name: 'DetalleFueraTiempo', params: { id: ticketId } });
      break;
  }
};

// * Formularios
const executeTransferTicker = () => {
  loading.value = true;

  store
    .dispatch(EXECUTE_TICKET_TRANSFER, {
      id: ticketId,
      payload: {
        endDate: moment(ticketData.value.startDate).subtract(1, 'd').format()
      }
    })
    .then(() => {
      notification(
        'success',
        'Correcto',
        'Ticket de transferencia ejecutado con éxito'
      );
      getTicketData();
    })
    .catch((err) => {
      notification('error', 'Error', err.response.data.message);
    })
    .finally(() => {
      loading.value = false;
    });
};

// Modal de cancelacion
const transferModalRef = ref();
const openCancelModal = () => {
  transferModalRef.value.openModal();
};

// Modal de Ejecucion
const executeModalRef = ref();
const openExecuteModal = () => {
  executeModalRef.value.openModal();
};

const cancelTransferTicker = () => {
  loading.value = true;

  store
    .dispatch(CANCEL_TICKET_TRANSFER, {
      id: ticketId,
      payload: {
        insuranceCompanyId: ticketData.value.insuranceCompany.id
      }
    })
    .then(() => {
      notification(
        'success',
        'Correcto',
        'Ticket de transferencia cancelado con éxito'
      );
      getTicketData();
    })
    .catch((err) => {
      notification('error', 'Error', err.response.data.message);
    })
    .finally(() => {
      loading.value = false;
    });
};

// Obtención de data principal
const ticketData = ref({});
const getTicketData = () => {
  store
    .dispatch(FETCH_TICKET_ID, ticketId)
    .then((resp) => {
      const { data } = resp;
      ticketData.value = data;
      insuranceMessage.value = data.message;

      getBroker(ticketData.value.broker.id).then((resp) => {
        brokerName.value = resp;
      });
    })
    .catch((err) => {
      console.log(err);
    });
};

const getTransferInformation = () => {
  store
    .dispatch(FETCH_TRANSFER_INFORMATION, ticketId)
    .then((resp) => {
      const {
        oldPolicies,
        oldCoverageGroupCompanies,
        newPolicies,
        newCoverageGroupCompanies
      } = resp.data;

      const originPolicies = oldPolicies.map((pol) => {
        return {
          ...pol,
          coverages: oldCoverageGroupCompanies
            .filter((cgc) => cgc.coverage.policyId === pol.id)
            .map((cgc) => ({
              ...cgc.coverage,
              groupName: cgc.groupCompany.name
            }))
        };
      });

      const destinyPolicies = newPolicies.map((pol) => {
        return {
          ...pol,
          coverages: newCoverageGroupCompanies
            .filter((cgc) => cgc.coverage.policyId === pol.id)
            .map((cgc) => ({
              ...cgc.coverage,
              groupName: cgc.groupCompany.name
            }))
        };
      });

      transferInfo.value = {
        originPolicies,
        destinyPolicies
      };
    })
    .catch((error) => {
      notification('error', 'Error', error.response.data.message);
    });
};

const getEmployeeData = () => {
  store
    .dispatch(FETCH_EMPLOYEE_TICKET, ticketId)
    .then((resp) => {
      employeeData.value = resp.data;
    })
    .catch((err) => {
      console.log(err);
    });
};

const getTransferGroups = () => {
  store
    .dispatch(FETCH_TRANSFER_GROUPS, ticketId)
    .then((resp) => {
      groupData.value = resp.data;
    })
    .catch((error) => {
      notification('error', 'Error', error.response.data.message);
    });
};

onMounted(() => {
  getTicketData();
  getEmployeeData();
  getCommentsByTicketId();
  getTicketOptionalFiles();
  getTransferInformation();
  getTransferGroups();
  getAllCasesByTicketId();
});
</script>
